import { dataService } from '../../services/dataService';

/** VAPID application server key arrives as base64url: pushManager.subscribe() requires a
 * raw Uint8Array, not the string. Standard conversion without external dependencies. */
const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

export const isPushSupported = (): boolean =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** Registers the service worker if not already registered: harmless, does not prompt for permission.
 * Called on application mount (App.tsx), regardless of whether user later activates push. */
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register('/service-worker.js');
  } catch (err) {
    console.error('Error registering service worker:', err);
    return null;
  }
};

export type PushSubscriptionState = 'unsupported' | 'denied' | 'subscribed' | 'not-subscribed';

/** Current push notification state on THIS device/browser (subscriptions are per device, not
 * per account; see pages/Perfil.tsx). */
export const getPushSubscriptionState = async (): Promise<PushSubscriptionState> => {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  if (!registration) return 'not-subscribed';
  const subscription = await registration.pushManager.getSubscription();
  return subscription ? 'subscribed' : 'not-subscribed';
};

/** Prompts for permission if needed, subscribes this device, and registers the subscription on
 * the backend. Throws if user denies permission or if subscription fails: caller (Perfil.tsx)
 * determines presentation. */
export const subscribeToPush = async (): Promise<void> => {
  if (!isPushSupported()) throw new Error('unsupported');

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('denied');

  const registration = await navigator.serviceWorker.ready;
  const { publicKey } = await dataService.getVapidPublicKey();

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource
    });
  }

  const json = subscription.toJSON();
  await dataService.subscribePush({
    endpoint: json.endpoint!,
    keys: { p256dh: json.keys!.p256dh, auth: json.keys!.auth }
  });
};

/** Cancels subscription for this device both locally and on the backend. */
export const unsubscribeFromPush = async (): Promise<void> => {
  if (!isPushSupported()) return;
  const registration = await navigator.serviceWorker.ready.catch(() => null);
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  await dataService.unsubscribePush(endpoint);
};
